import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ArrowRight } from "lucide-react";
import { useLang } from "@/context";
import { T, linkTo, alternatePath } from "@/i18n";
import { useLocation } from "react-router-dom";
import { WHATSAPP_NUMBER } from "@/config";

export const BaobabLogo = ({ light = false }) => {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <Link to={linkTo(lang, "home")} className="flex items-center gap-2.5 group" data-testid="brand-logo">
      <svg width="34" height="34" viewBox="0 0 48 48" fill="none" aria-hidden="true" className="shrink-0">
        <path d="M24 44V22" stroke="#B5472B" strokeWidth="3.4" strokeLinecap="round" />
        <path d="M24 26C20 20 14 18 9 19M24 24C26 17 22 12 17 9M24 23C25 16 30 11 36 10M24 26C29 21 35 20 40 22M24 28C21 24 16 23 12 25" stroke={light ? "#F6EFE4" : "#0E4D5C"} strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="17" cy="8" r="2.2" fill="#B5472B" /><circle cx="36" cy="9" r="2.2" fill="#B5472B" />
        <circle cx="9" cy="18" r="2.2" fill="#2FA3A0" /><circle cx="40" cy="21" r="2.2" fill="#2FA3A0" />
        <path d="M16 44h16" stroke={light ? "#F6EFE4" : "#1F2328"} strokeWidth="2.6" strokeLinecap="round" />
      </svg>
      <span className={`font-serif text-lg sm:text-xl leading-none ${light ? "text-[#F6EFE4]" : "text-[#0E4D5C]"}`}>
        {lang === "it" ? <>Investire in <em className="not-italic text-[#B5472B]">Madagascar</em></> : <>Invest in <em className="not-italic text-[#B5472B]">Madagascar</em></>}
      </span>
    </Link>
  );
};

export function Header() {
  const { lang } = useLang();
  const t = T[lang];
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const nav = [
    ["properties", t.nav.properties],
    ["farmland", t.nav.farmland],
    ["services", t.nav.services],
    ["zones", t.nav.zones],
    ["guides", t.nav.guides],
    ["tools", t.nav.tools],
  ];
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#F6EFE4]/85 backdrop-blur-xl border-b border-[#0E4D5C]/10" data-testid="site-header">
      <div className="container-site flex items-center justify-between py-3.5">
        <BaobabLogo />
        <nav className="hidden lg:flex items-center gap-7" aria-label="Main">
          {nav.map(([key, label]) => (
            <Link key={key} to={linkTo(lang, key)} data-testid={`nav-${key}`}
              className="text-sm font-medium text-[#1F2328]/75 hover:text-[#B5472B] transition-colors duration-200">
              {label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to={alternatePath(location.pathname, lang)} data-testid="language-switcher"
            className="flex items-center gap-1.5 text-xs font-semibold tracking-wider text-[#0E4D5C] border border-[#0E4D5C]/25 rounded-full px-3 py-1.5 hover:bg-[#0E4D5C] hover:text-[#F6EFE4] transition-colors duration-200">
            <Globe size={14} /> {lang === "it" ? "EN" : "IT"}
          </Link>
          <Link to={linkTo(lang, "contact")} className="btn-primary !py-2 !px-5 hidden md:inline-flex" data-testid="header-cta">
            {t.nav.cta} <ArrowRight size={15} />
          </Link>
          <button className="lg:hidden p-2 text-[#0E4D5C]" onClick={() => setOpen(!open)} aria-label={t.common.menu} data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-[#0E4D5C]/10 bg-[#F6EFE4] px-6 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {[...nav, ["about", t.nav.about], ["howItWorks", t.nav.howItWorks], ["faq", t.nav.faq], ["contact", t.nav.contact], ["sell", t.nav.sell]].map(([key, label]) => (
            <Link key={key} to={linkTo(lang, key)} onClick={() => setOpen(false)} className="text-base font-medium text-[#1F2328]/80 py-1">
              {label}
            </Link>
          ))}
          <Link to={linkTo(lang, "contact")} onClick={() => setOpen(false)} className="btn-primary mt-2">{t.nav.cta}</Link>
        </nav>
      )}
    </header>
  );
}

export function Footer() {
  const { lang } = useLang();
  const t = T[lang];
  const location = useLocation();
  const [nlEmail, setNlEmail] = useState("");
  const [nlDone, setNlDone] = useState(false);
  const submitNl = async (e) => {
    e.preventDefault();
    if (!nlEmail.includes("@")) return;
    try {
      const api = (await import("@/api")).default;
      await api.post("/leads", { type: "newsletter", email: nlEmail, privacy: true, lang, page: location.pathname });
      setNlDone(true);
    } catch { setNlDone(true); }
  };
  const colExplore = [["properties", t.nav.properties], ["farmland", t.nav.farmland], ["zones", t.nav.zones], ["services", t.nav.services], ["guides", t.nav.guides], ["tools", t.nav.tools]];
  const colCompany = [["about", t.nav.about], ["howItWorks", t.nav.howItWorks], ["testimonials", t.nav.testimonials], ["faq", t.nav.faq], ["contact", t.nav.contact], ["sell", t.nav.sell]];
  const colLegal = [["privacy", "Privacy"], ["cookies", "Cookie"], ["terms", lang === "it" ? "Termini" : "Terms"], ["risks", lang === "it" ? "Avvertenze sui rischi" : "Risk disclosure"]];
  return (
    <footer className="bg-[#0E4D5C] text-[#F6EFE4]" data-testid="site-footer">
      <div className="container-site py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <BaobabLogo light />
          <p className="mt-4 text-sm text-[#F6EFE4]/70 max-w-sm leading-relaxed">{t.footer.tagline}</p>
          <div className="mt-6">
            <p className="text-sm font-semibold">{t.footer.newsletterTitle}</p>
            <p className="text-xs text-[#F6EFE4]/60 mt-1">{t.footer.newsletterSub}</p>
            {nlDone ? (
              <p className="mt-3 text-sm text-[#E8B64C]" data-testid="newsletter-success">{t.form.newsletterSuccess}</p>
            ) : (
              <form onSubmit={submitNl} className="mt-3 flex gap-2 max-w-sm" data-testid="newsletter-form">
                <input type="email" required value={nlEmail} onChange={(e) => setNlEmail(e.target.value)}
                  placeholder={t.form.guideEmail} aria-label={t.form.email} data-testid="newsletter-email-input"
                  className="flex-1 rounded-full bg-[#F6EFE4]/10 border border-[#F6EFE4]/25 px-4 py-2.5 text-sm placeholder-[#F6EFE4]/50 focus:outline-none focus:border-[#E8B64C]" />
                <button type="submit" className="rounded-full bg-[#B5472B] px-5 py-2.5 text-sm font-medium hover:bg-[#93391f] transition-colors" data-testid="newsletter-submit-button">
                  {t.form.newsletterSubmit}
                </button>
              </form>
            )}
          </div>
        </div>
        {[[t.footer.explore, colExplore, "explore"], [t.footer.company, colCompany, "company"], [t.footer.legal, colLegal, "legal"]].map(([title, items, id]) => (
          <div key={id}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E8B64C]">{title}</p>
            <ul className="mt-4 space-y-2.5">
              {items.map(([key, label]) => (
                <li key={key}><Link to={linkTo(lang, key)} className="text-sm text-[#F6EFE4]/75 hover:text-[#F6EFE4] transition-colors" data-testid={`footer-${id}-${key}`}>{label}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-[#F6EFE4]/10">
        <div className="container-site py-6 space-y-3">
          <p className="text-xs text-[#F6EFE4]/55 leading-relaxed max-w-4xl" data-testid="footer-disclaimer">{t.footer.disclaimer}</p>
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-[#F6EFE4]/50">
            <span data-testid="footer-company-data">{t.footer.companyData}</span>
            <span>© {new Date().getFullYear()} {t.brand}. {t.footer.rights}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function WhatsAppFloat() {
  const { lang } = useLang();
  const location = useLocation();
  const msg = lang === "it"
    ? `Salve! Vi scrivo dalla pagina ${location.pathname} del sito Investire in Madagascar.`
    : `Hello! I'm writing from page ${location.pathname} of the Invest in Madagascar website.`;
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`}
      target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="whatsapp-float"
      className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#2FA3A0] text-white shadow-xl hover:scale-110 transition-transform duration-300"
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2Zm5.5 14.1c-.2.7-1.3 1.3-1.9 1.3-.5.1-1.1.1-1.8-.1-2.6-.8-4.4-2.6-5.7-4.5-.9-1.3-1.3-2.7-.8-3.6.2-.5.7-.9 1.1-.9h.6c.2 0 .4 0 .6.5l.8 1.9c.1.2 0 .4-.1.6l-.5.7c-.1.2-.2.4 0 .7.5.9 1.6 2 2.9 2.7.3.2.5.1.7-.1l.7-.8c.2-.2.4-.2.6-.1l1.8.9c.3.1.5.2.5.4 0 .1 0 .3-.1.5Z" />
      </svg>
    </a>
  );
}
